// features/workstations/qqq/intel/pre-breakout-patterns.js
// ═══════════════════════════════════════════════════════════
// Pre-Breakout Pattern Research — Intelligence-tab view
// ═══════════════════════════════════════════════════════════
//
// Per docs/QQQ_PRE_BREAKOUT_PATTERN_RESEARCH.md and Tim's request
// 2026-04-29 to replace the legacy Intelligence-tab cards with the
// pre-breakout pattern findings surface.
//
// Layout:
//   - Threshold selector  ($3 / $4 / $5 / $6 / $7 toggle)
//   - Pipeline status strip (bars loaded / events detected / clusters built)
//   - Pattern cluster cards (one per cluster with up/down/reversal
//     stats, sample dates, Tim-annotation slot)
//   - Date list table (recent breakouts at this threshold for TOS
//     verification)
//
// Empty-state aware: when the discovery pipeline hasn't ingested
// data yet, surfaces a clear progress card with "Run discovery"
// button instead of pretending to have findings.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';

const THRESHOLDS = [3, 4, 5, 6, 7];

export default function PreBreakoutPatternsView() {
  const [threshold, setThreshold] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const [discoverMessage, setDiscoverMessage] = useState(null);
  const [activeClusterId, setActiveClusterId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ threshold: String(threshold) });
      if (activeClusterId !== null) params.set('cluster_id', String(activeClusterId));
      const res = await fetch(`/api/qqq/breakout-patterns?${params}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [threshold, activeClusterId]);

  useEffect(() => { load(); }, [load]);

  const triggerDiscovery = async () => {
    setDiscovering(true);
    setDiscoverMessage(null);
    try {
      const res = await fetch('/api/qqq/breakout-patterns/discover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: 'all' }),
      });
      const json = await res.json();
      setDiscoverMessage(json.message || `Discovery: ${json.status || 'unknown'}`);
      // Refresh the read view in case anything updated
      load();
    } catch (err) {
      setDiscoverMessage(`Discovery error: ${err.message}`);
    } finally {
      setDiscovering(false);
    }
  };

  const clusters = data?.clusters || [];
  const events = data?.events || [];
  const status = data?.status || {};
  const pipelineStatus = data?.pipeline_status || 'unknown';

  return (
    <div className="qqq-breakouts">
      <div className="qqq-breakouts__header">
        <div className="qqq-breakouts__title-block">
          <div className="qqq-breakouts__eyebrow">Pre-Breakout Pattern Research</div>
          <div className="qqq-breakouts__title">
            Antecedent-mined patterns from 2 years of QQQ 5-min data
          </div>
          <div className="qqq-breakouts__hint">
            For each $-threshold breakout, the system reverse-engineers what preceded it,
            clusters the shapes, and tabulates resolution direction.
          </div>
        </div>
        <button
          type="button"
          className="qqq-breakouts__discover-btn"
          onClick={triggerDiscovery}
          disabled={discovering}
        >
          {discovering ? 'Running…' : 'Run discovery pipeline'}
        </button>
      </div>

      {discoverMessage && (
        <div className="qqq-breakouts__discover-message">{discoverMessage}</div>
      )}

      {/* Threshold selector */}
      <div className="qqq-breakouts__threshold-row">
        <span className="qqq-breakouts__threshold-label">Threshold</span>
        {THRESHOLDS.map(t => (
          <button
            key={t}
            type="button"
            className={`qqq-breakouts__threshold-btn${t === threshold ? ' is-active' : ''}`}
            onClick={() => { setThreshold(t); setActiveClusterId(null); }}
          >
            ${t}
          </button>
        ))}
      </div>

      {/* Pipeline status strip */}
      <PipelineStatusStrip status={status} pipelineStatus={pipelineStatus} threshold={threshold} />

      {error && (
        <div className="qqq-breakouts__error">Error loading patterns: {error}</div>
      )}
      {loading && !data && (
        <div className="qqq-breakouts__loading">Loading patterns at ${threshold} threshold…</div>
      )}

      {/* Cluster cards */}
      {!loading && clusters.length > 0 && (
        <div className="qqq-breakouts__clusters">
          <div className="qqq-breakouts__section-header">
            Pattern Clusters · ${threshold} threshold · {clusters.length}{' '}
            {clusters.length === 1 ? 'cluster' : 'clusters'}
          </div>
          <div className="qqq-breakouts__cluster-grid">
            {clusters.map(c => (
              <ClusterCard
                key={c.id}
                cluster={c}
                isActive={c.cluster_id === activeClusterId}
                onClick={() => setActiveClusterId(c.cluster_id === activeClusterId ? null : c.cluster_id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state when no clusters yet */}
      {!loading && clusters.length === 0 && pipelineStatus !== 'tables_not_migrated' && (
        <EmptyClusterState pipelineStatus={pipelineStatus} status={status} />
      )}

      {/* Date list */}
      {!loading && events.length > 0 && (
        <div className="qqq-breakouts__events">
          <div className="qqq-breakouts__section-header">
            {activeClusterId !== null
              ? `Events in cluster ${activeClusterId} · ${events.length}`
              : `Recent breakout events at $${threshold} · ${events.length}`}
          </div>
          <EventTable events={events} />
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────

function PipelineStatusStrip({ status, pipelineStatus, threshold }) {
  return (
    <div className="qqq-breakouts__status-strip">
      <StatusPill label="Bars loaded" value={fmtCount(status.bars_loaded)}
        ok={status.bars_loaded > 0} />
      <StatusPill label={`Events @ $${threshold}`} value={fmtCount(status.events_detected)}
        ok={status.events_detected > 0} />
      <StatusPill label="Clusters" value={fmtCount(status.clusters_built)}
        ok={status.clusters_built > 0} />
      <StatusPill label="Pipeline" value={humanizeStatus(pipelineStatus)}
        ok={pipelineStatus === 'ready'} />
    </div>
  );
}

function StatusPill({ label, value, ok }) {
  return (
    <div className={`qqq-breakouts__status-pill${ok ? ' is-ok' : ''}`}>
      <span className="qqq-breakouts__status-pill-label">{label}</span>
      <span className="qqq-breakouts__status-pill-value">{value}</span>
    </div>
  );
}

function ClusterCard({ cluster, isActive, onClick }) {
  const upPct = num(cluster.resolution_up_pct, 0);
  const downPct = num(cluster.resolution_down_pct, 0);
  const reversalPct = num(cluster.resolution_reversal_pct, 0);
  const dominant = upPct >= downPct
    ? { label: 'UP', tone: 'sage', pct: upPct }
    : { label: 'DOWN', tone: 'terra', pct: downPct };
  const name = cluster.human_name || cluster.auto_name || `Cluster ${cluster.cluster_id}`;

  return (
    <button
      type="button"
      className={`qqq-breakouts__cluster-card qqq-breakouts__cluster-card--${dominant.tone}${isActive ? ' is-active' : ''}`}
      onClick={onClick}
    >
      <div className="qqq-breakouts__cluster-name">{name}</div>
      <div className="qqq-breakouts__cluster-headline">
        {dominant.pct.toFixed(0)}% {dominant.label}
        <span className="qqq-breakouts__cluster-n"> · n={cluster.n_instances}</span>
      </div>
      <div className="qqq-breakouts__cluster-stats">
        <span className="qqq-breakouts__cluster-stat qqq-breakouts__cluster-stat--up">↑ {upPct.toFixed(0)}%</span>
        <span className="qqq-breakouts__cluster-stat qqq-breakouts__cluster-stat--down">↓ {downPct.toFixed(0)}%</span>
        <span className="qqq-breakouts__cluster-stat qqq-breakouts__cluster-stat--rev">↔ {reversalPct.toFixed(0)}%</span>
      </div>
      <div className="qqq-breakouts__cluster-magnitudes">
        avg {dominant.label === 'UP' ? `+$${num(cluster.avg_up_magnitude, 0).toFixed(2)}` : `-$${num(cluster.avg_down_magnitude, 0).toFixed(2)}`}
        {cluster.avg_duration_min ? ` · ${Math.round(cluster.avg_duration_min)}m` : ''}
      </div>
      {cluster.notes && (
        <div className="qqq-breakouts__cluster-notes">{cluster.notes}</div>
      )}
      {Array.isArray(cluster.context_tags) && cluster.context_tags.length > 0 && (
        <div className="qqq-breakouts__cluster-tags">
          {cluster.context_tags.map(t => (
            <span key={t} className="qqq-breakouts__cluster-tag">{t}</span>
          ))}
        </div>
      )}
    </button>
  );
}

function EventTable({ events }) {
  return (
    <div className="qqq-breakouts__event-table">
      <div className="qqq-breakouts__event-row qqq-breakouts__event-row--header">
        <span>Date</span>
        <span>Start</span>
        <span>Dir</span>
        <span>Peak</span>
        <span>Dur</span>
        <span>Pre-setup</span>
        <span>Cluster</span>
        <span>Reversal</span>
      </div>
      {events.map(e => (
        <div key={e.id || `${e.event_date}-${e.breakout_start_at}`} className="qqq-breakouts__event-row">
          <span className="qqq-breakouts__event-date">{e.event_date}</span>
          <span className="qqq-breakouts__event-time">{fmtETTime(e.breakout_start_at)}</span>
          <span className={`qqq-breakouts__event-dir qqq-breakouts__event-dir--${e.direction}`}>
            {e.direction === 'up' ? '↑ UP' : '↓ DOWN'}
          </span>
          <span className="qqq-breakouts__event-peak">${num(e.peak_magnitude, 0).toFixed(2)}</span>
          <span className="qqq-breakouts__event-dur">{e.duration_to_peak_min}m</span>
          <span className="qqq-breakouts__event-presetup">{fmtPresetup(e.pre_setup_duration_min)}</span>
          <span className="qqq-breakouts__event-cluster">
            {e.cluster_label || (e.cluster_id != null ? `#${e.cluster_id}` : 'unclustered')}
          </span>
          <span className={`qqq-breakouts__event-rev${e.reversal_status ? ' is-yes' : ''}`}>
            {e.reversal_status ? 'reversed' : '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

function EmptyClusterState({ pipelineStatus, status }) {
  const messages = {
    awaiting_bar_ingestion:
      'Stage 1 of the discovery pipeline (2-year QQQ 5-min bar ingestion) hasn’t run. Bars are the prerequisite for everything below — without them no breakouts can be detected.',
    awaiting_breakout_detection:
      `Stage 2 (breakout detection) hasn’t run. ${status.bars_loaded.toLocaleString()} bars are loaded; the detector will scan them for $3-$7 events and compute the 22-feature pre-setup vector for each.`,
    awaiting_clustering:
      `Stage 3 (k-means clustering) hasn’t run. ${status.events_detected} events are detected; clustering groups them into pattern families.`,
    unknown: 'Pipeline status unknown.',
  };
  return (
    <div className="qqq-breakouts__empty">
      <div className="qqq-breakouts__empty-headline">No clusters yet at this threshold.</div>
      <div className="qqq-breakouts__empty-body">{messages[pipelineStatus] || messages.unknown}</div>
      <div className="qqq-breakouts__empty-roadmap">
        <span className="qqq-breakouts__empty-roadmap-step">Bar ingestion</span>
        <span className="qqq-breakouts__empty-roadmap-arrow">→</span>
        <span className="qqq-breakouts__empty-roadmap-step">Breakout detection</span>
        <span className="qqq-breakouts__empty-roadmap-arrow">→</span>
        <span className="qqq-breakouts__empty-roadmap-step">Clustering</span>
        <span className="qqq-breakouts__empty-roadmap-arrow">→</span>
        <span className="qqq-breakouts__empty-roadmap-step">Annotation</span>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────

function num(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function fmtCount(n) {
  if (!n) return '0';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function fmtETTime(iso) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch (_e) {
    return '';
  }
}

function fmtPresetup(min) {
  if (!Number.isFinite(min)) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function humanizeStatus(s) {
  switch (s) {
    case 'ready': return 'Ready';
    case 'tables_not_migrated': return 'Tables not migrated';
    case 'awaiting_bar_ingestion': return 'Bars pending';
    case 'awaiting_breakout_detection': return 'Detection pending';
    case 'awaiting_clustering': return 'Clustering pending';
    default: return s || 'Unknown';
  }
}
